import React from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * Small square photo thumbnail for a catalog/material row. Falls back to the
 * material family icon (`token` for filament / `water_drop` for resin) when the
 * row has no image. Shared by the tenant catalog browse table, the "add from
 * catalog" inventory picker, and the platform-admin global catalog tables.
 *
 * Kept separate from the gallery `PhotoThumb`, which is a heavier card with
 * hover/action overlays unsuited to a dense table cell.
 */
export interface CatalogThumbProps {
  url?: string | null;
  /** Material Symbols name shown when there is no image. */
  icon: IconName;
  alt: string;
}

const CatalogThumb: React.FC<CatalogThumbProps> = ({ url, icon, alt }) => {
  // Fall back to the icon if the image is missing or fails to load. The catalog
  // image server 302s straight to R2 without an existence HEAD (that per-image
  // WAN check was dropped to stop it starving the admin), so a missing cover
  // now surfaces here as a load error instead of an upstream 404 — swap in the
  // placeholder icon rather than showing a broken image.
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [url]);
  return (
    <div className="w-10 h-10 rounded-md overflow-hidden border border-border-subtle dark:border-border-subtle-dark bg-slate-50 dark:bg-surface-hover-dark flex items-center justify-center shrink-0">
      {url && !failed ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <Icon name={icon} className="!text-[18px] text-slate-300 dark:text-slate-600" />
      )}
    </div>
  );
};

export default CatalogThumb;
